/**
 * live-vision 소비 접합부 (features/live-vision — PORTING.fe.md §2)
 *
 * 계약·좌표 수학·오버레이 정본은 전부 플러그인 패키지가 제공한다 — 여기서는
 * 프로젝트 관례(convertFileSrc, 세션 수명 관리)만 얇게 감싼다. cover 매핑이나
 * 보간을 재작성하지 말 것 (B-3: mapQuadToView / QuadOverlay 가 정본).
 */
import { Capacitor } from "@capacitor/core";
import {
  LiveVision,
  QuadOverlay,
  mapQuadToView,
  type CaptureAndCropResult,
  type DetectionEvent,
  type QuadCorners,
  type ScanStateEvent,
  type StartScanOptions,
} from "@duckflex/capacitor-live-vision";

export type { DetectionEvent, ScanStateEvent, CaptureAndCropResult };
export { LiveVision, QuadOverlay, mapQuadToView };

export interface ScanSessionHandle {
  sessionId: string;
  overlay: QuadOverlay;
  stop(): Promise<void>;
}

/**
 * 스캔 세션 시작 + 오버레이 배선 + 리스너 수명 관리를 한 번에.
 * scanState 'interrupted' 복귀 UX(재시작 버튼 등)는 onInterrupted 로 앱이 처리한다 (E4).
 */
export async function startScanSession(options: {
  canvas: HTMLCanvasElement;
  scan?: StartScanOptions;
  /** 가이드 프레임 오버라이드 (프레임 정규화 좌표) — 미지정 시 플러그인 기본 */
  guide?: QuadCorners;
  /**
   * false 면 detection 이벤트를 오버레이에 전달하지 않는다 — 가이드가 검출 quad 로
   * morph 하지 않고 고정 표시. 검출 시각화는 onDetection 으로 앱이 직접 그린다.
   */
  updateOverlay?: boolean;
  onDetection?: (ev: DetectionEvent) => void;
  onInterrupted?: (reason: ScanStateEvent["reason"]) => void;
}): Promise<ScanSessionHandle> {
  const overlay = new QuadOverlay(
    options.canvas,
    options.guide ? { guide: options.guide } : {},
  );
  const detection = await LiveVision.addListener("detection", (ev) => {
    if (options.updateOverlay !== false) overlay.update(ev);
    options.onDetection?.(ev);
  });
  const scanState = await LiveVision.addListener("scanState", (ev) => {
    if (ev.state === "interrupted") options.onInterrupted?.(ev.reason);
  });

  let sessionId: string;
  try {
    ({ sessionId } = await LiveVision.startScan(options.scan));
  } catch (err) {
    // 시작 실패 시 위에서 등록한 리스너를 반드시 회수 — 폴백/재시도마다 리스너가
    // 누적되는 누수 방지 (⚠ 템플릿 원본 features/live-vision/fe 에도 동일 수정 필요)
    overlay.stop();
    await detection.remove().catch(() => undefined);
    await scanState.remove().catch(() => undefined);
    throw err;
  }
  overlay.setSession(sessionId);
  overlay.start();

  return {
    sessionId,
    overlay,
    stop: async () => {
      overlay.stop();
      await LiveVision.stopScan().catch(() => undefined); // 이미 interrupted 였으면 무시
      await detection.remove();
      await scanState.remove();
    },
  };
}

/** captureAndCrop path 결과를 <img src> 로 쓸 수 있는 URL 로 변환 */
export function cropSrc(result: CaptureAndCropResult): string | null {
  if (result.cropPath) return Capacitor.convertFileSrc(result.cropPath);
  if (result.cropBase64) return `data:image/jpeg;base64,${result.cropBase64}`;
  return null;
}
